import React, { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';

const AgentAuthContext = createContext(null);

export const useAgentAuth = () => {
  const ctx = useContext(AgentAuthContext);
  if (!ctx) throw new Error('useAgentAuth must be used within an AgentAuthProvider');
  return ctx;
};

export const AgentAuthProvider = ({ children }) => {
  const [agentUser, setAgentUser] = useState(null);
  const [agentToken, setAgentToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const tk = localStorage.getItem('agentAuthToken');
      const usr = localStorage.getItem('agentAuthUser');
      if (tk && usr) {
        const parsed = JSON.parse(usr);
        if (['agent', 'sub-agent'].includes(parsed.userType)) {
          setAgentToken(tk);
          setAgentUser(parsed);
        } else {
          localStorage.removeItem('agentAuthToken');
          localStorage.removeItem('agentAuthUser');
        }
      }
    } catch {
      localStorage.removeItem('agentAuthToken');
      localStorage.removeItem('agentAuthUser');
    } finally {
      setLoading(false);
    }
  }, []);

  const agentLogin = async (email, password) => {
    try {
      const res = await authAPI.login({ email, password, rememberMe: true });
      if (res.success && ['agent', 'sub-agent'].includes(res.user?.userType)) {
        setAgentToken(res.token);
        setAgentUser(res.user);
        localStorage.setItem('agentAuthToken', res.token);
        localStorage.setItem('agentAuthUser', JSON.stringify(res.user));
        return { success: true, user: res.user };
      }
      if (res.success && !['agent', 'sub-agent'].includes(res.user?.userType)) {
        return { success: false, message: 'Accesso negato. Questo account non è un agente.' };
      }
      return { success: false, message: res.message || 'Credenziali non valide' };
    } catch (e) {
      return { success: false, message: e.response?.data?.detail || 'Errore durante l\'accesso' };
    }
  };

  const agentLogout = async () => {
    try {
      if (agentToken) await authAPI.logout(agentToken);
    } catch { /* ignore */ }
    finally {
      setAgentToken(null);
      setAgentUser(null);
      localStorage.removeItem('agentAuthToken');
      localStorage.removeItem('agentAuthUser');
    }
  };

  const isAgentAuthenticated = () => !!(agentUser && agentToken && ['agent', 'sub-agent'].includes(agentUser.userType));

  return (
    <AgentAuthContext.Provider value={{ agentUser, agentToken, agentLogin, agentLogout, isAgentAuthenticated, loading }}>
      {children}
    </AgentAuthContext.Provider>
  );
};
